import {
  REPORT_LABELS,
  type ReportKind,
  REPORT_REASONS,
  REPORT_REASON_LABELS,
} from '@bienenkampf/shared';
import { Badge, Button, Card, Input } from '@bienenkampf/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNow } from '../hooks/useNow.js';
import { formatAbsRel, formatClock, formatDateTime } from '../lib/time.js';
import { useTRPC } from '../lib/trpc.js';

const COORD_RE = /\[(-?\d{1,3})\|(-?\d{1,3})\]/g;

/** Text mit [q|r]-Koordinaten als Karten-Links rendern. */
export function RichText({ text, worldId }: { text: string; worldId: number }) {
  const parts: ReactNode[] = [];
  let last = 0;
  for (const m of text.matchAll(COORD_RE)) {
    const i = m.index ?? 0;
    if (i > last) parts.push(text.slice(last, i));
    parts.push(
      <Link
        key={`${i}`}
        to="/w/$worldId"
        params={{ worldId: String(worldId) }}
        search={{ q: Number(m[1]), r: Number(m[2]) }}
        className="rounded bg-stone-800 px-1 font-mono text-amber-300 hover:bg-stone-700"
      >
        [{m[1]}|{m[2]}]
      </Link>,
    );
    last = i + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <span className="whitespace-pre-wrap break-words">{parts}</span>;
}

export function ChatPage() {
  const { worldId: worldIdStr } = useParams({ from: '/w/$worldId' });
  const worldId = Number(worldIdStr);
  const search = useSearch({ from: '/w/$worldId/chat' });
  const navigate = useNavigate();
  const trpc = useTRPC();
  const convs = useQuery({
    ...trpc.chat.conversations.queryOptions({ worldId }),
    refetchInterval: 30_000,
  });
  const selectedId = search.c ?? null;
  const select = (id: number | null) =>
    navigate({
      to: '/w/$worldId/chat',
      params: { worldId: worldIdStr },
      search: id ? { c: id } : {},
    });

  // Standard: erstes Gespraech (Desktop)
  useEffect(() => {
    if (selectedId === null && convs.data && convs.data.length > 0 && window.innerWidth >= 1024) {
      void select(convs.data[0]!.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convs.data, selectedId]);

  const selected = convs.data?.find((c) => c.id === selectedId) ?? null;

  return (
    <div className="flex flex-col gap-3 lg:grid lg:grid-cols-[18rem_1fr] lg:items-start">
      <Card
        title="Chat"
        className={selectedId ? 'hidden lg:block' : ''}
        action={
          <Link
            to="/w/$worldId/stand"
            params={{ worldId: worldIdStr }}
            className="text-sm text-amber-400"
          >
            Stand ⇢
          </Link>
        }
      >
        {convs.isPending && <p className="text-sm text-stone-400">Lade…</p>}
        {convs.data?.length === 0 && (
          <p className="text-sm text-stone-400">
            Noch keine Gespräche. Tritt einem{' '}
            <Link
              to="/w/$worldId/stand"
              params={{ worldId: worldIdStr }}
              className="text-amber-400"
            >
              Stand
            </Link>{' '}
            bei oder schreib einer Königin von der Karte aus.
          </p>
        )}
        <ul className="flex flex-col divide-y divide-stone-800">
          {convs.data?.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => void select(c.id)}
                className={`flex w-full items-center gap-2 py-2 text-left ${c.id === selectedId ? 'text-amber-300' : ''}`}
              >
                <span className="text-base">
                  {c.kind === 'DM' ? '✉' : c.councilOnly ? '🏛' : '#'}
                </span>
                <span className="min-w-0 flex-1">
                  <span className={`block truncate text-sm ${c.unread > 0 ? 'font-semibold' : ''}`}>
                    {c.kind === 'ALLIANCE_CHANNEL' ? c.title.replace(/^#/, '') : c.title}
                    {c.allianceTag && c.kind === 'ALLIANCE_CHANNEL' && (
                      <span className="ml-1 text-xs text-stone-500">[{c.allianceTag}]</span>
                    )}
                  </span>
                  <span className="block truncate text-xs text-stone-500">{c.lastBody ?? '–'}</span>
                </span>
                {c.unread > 0 && (
                  <span className="rounded-full bg-amber-500 px-1.5 text-[10px] font-semibold text-stone-950">
                    {c.unread}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      </Card>
      <div className={selectedId ? '' : 'hidden lg:block'}>
        {selected ? (
          <Thread worldId={worldId} conversation={selected} onBack={() => void select(null)} />
        ) : (
          <Card>
            <p className="text-sm text-stone-400">Wähle links ein Gespräch.</p>
          </Card>
        )}
      </div>
    </div>
  );
}

type Conversation = NonNullable<ReturnType<typeof useConversations>['data']>[number];
function useConversations() {
  const trpc = useTRPC();
  const { worldId } = useParams({ from: '/w/$worldId' });
  return useQuery(trpc.chat.conversations.queryOptions({ worldId: Number(worldId) }));
}

function Thread({
  worldId,
  conversation,
  onBack,
}: {
  worldId: number;
  conversation: Conversation;
  onBack: () => void;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const now = useNow(15_000);
  const me = useQuery(trpc.auth.me.queryOptions());
  const myPlayerId = me.data?.players.find((p) => p.worldId === worldId)?.id;
  const msgs = useQuery({
    ...trpc.chat.messages.queryOptions({ worldId, conversationId: conversation.id, limit: 80 }),
    refetchInterval: 20_000,
  });
  const markRead = useMutation(
    trpc.chat.markRead.mutationOptions({
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: trpc.chat.conversations.pathKey() });
        void qc.invalidateQueries({ queryKey: trpc.chat.unread.pathKey() });
      },
    }),
  );
  const [body, setBody] = useState('');
  const send = useMutation(
    trpc.chat.send.mutationOptions({
      onSuccess: () => {
        setBody('');
        void qc.invalidateQueries({
          queryKey: trpc.chat.messages.queryKey({
            worldId,
            conversationId: conversation.id,
            limit: 80,
          }),
        });
        void qc.invalidateQueries({ queryKey: trpc.chat.conversations.pathKey() });
      },
    }),
  );
  const bottom = useRef<HTMLDivElement>(null);
  const lastId = msgs.data?.items.at(-1)?.id;
  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' });
    if (lastId && conversation.unread > 0)
      markRead.mutate({ worldId, conversationId: conversation.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastId, conversation.id]);

  const grouped = useMemo(() => msgs.data?.items ?? [], [msgs.data]);

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <button
            type="button"
            onClick={onBack}
            className="lg:hidden text-stone-400"
            aria-label="Zurück"
          >
            ←
          </button>
          {conversation.kind === 'ALLIANCE_CHANNEL' ? conversation.title : conversation.title}
          {conversation.councilOnly && <Badge tone="amber">Rat</Badge>}
          {conversation.kind === 'DM' && conversation.partnerId != null && (
            <BlockMenu worldId={worldId} partnerId={conversation.partnerId} />
          )}
        </span>
      }
      className="flex h-[70dvh] flex-col lg:h-[calc(100dvh-9.5rem)]"
    >
      <div className="min-h-0 flex-1 overflow-y-auto pr-1">
        {msgs.data?.hasMore && (
          <p className="mb-2 text-center text-xs text-stone-500">
            Ältere Nachrichten werden nicht geladen (Verlauf folgt).
          </p>
        )}
        {grouped.length === 0 && !msgs.isPending && (
          <p className="text-sm text-stone-400">Noch keine Nachrichten. Sag Hallo.</p>
        )}
        <ul className="flex flex-col gap-2">
          {grouped.map((m) => {
            const mine = m.senderId === myPlayerId;
            return (
              <li key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${mine ? 'bg-amber-500/15' : 'bg-stone-900'}`}
                >
                  {!mine && (
                    <div className="mb-0.5 text-xs font-medium text-amber-300">
                      {m.senderName ?? '–'}
                    </div>
                  )}
                  {m.body && <RichText text={m.body} worldId={worldId} />}
                  {m.attachments.map((a, i) => (
                    <AttachmentCard key={i} a={a} worldId={worldId} />
                  ))}
                  <div
                    className="mt-1 text-right text-[10px] text-stone-500"
                    title={formatDateTime(m.createdAt)}
                  >
                    {formatClock(m.createdAt)} ·{' '}
                    {formatAbsRel(m.createdAt, now).replace(/^.*\(/, '').replace(/\)$/, '')}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
        <div ref={bottom} />
      </div>
      <form
        className="mt-3 flex items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (body.trim()) send.mutate({ worldId, conversationId: conversation.id, body });
        }}
      >
        <div className="flex-1">
          <Input
            name="body"
            placeholder="Nachricht… Koordinaten wie [12|-4] werden zu Karten-Links"
            value={body}
            maxLength={2000}
            onChange={(e) => setBody(e.target.value)}
            autoComplete="off"
          />
        </div>
        <Button type="submit" disabled={!body.trim() || send.isPending} loading={send.isPending}>
          Senden
        </Button>
      </form>
      {send.error && <p className="mt-1 text-sm text-red-400">{send.error.message}</p>}
    </Card>
  );
}

function AttachmentCard({
  a,
  worldId,
}: {
  a: { type: string; [k: string]: unknown };
  worldId: number;
}) {
  if (a.type === 'report') {
    return (
      <Link
        to="/w/$worldId/berichte"
        params={{ worldId: String(worldId) }}
        search={{ token: String(a.token) }}
        className="mt-1 block rounded-lg border border-stone-700 bg-stone-950/60 px-3 py-2 text-xs hover:border-amber-500"
      >
        <span className="text-stone-400">{REPORT_LABELS[a.kind as ReportKind] ?? 'Bericht'}</span>
        <span className="block font-medium text-stone-100">{String(a.title)}</span>
      </Link>
    );
  }
  if (a.type === 'coord' || a.type === 'field') {
    return (
      <Link
        to="/w/$worldId"
        params={{ worldId: String(worldId) }}
        search={{ q: Number(a.q), r: Number(a.r) }}
        className="mt-1 block rounded-lg border border-stone-700 bg-stone-950/60 px-3 py-2 text-xs hover:border-amber-500"
      >
        📍 {String(a.label ?? '')} [{String(a.q)}|{String(a.r)}]
      </Link>
    );
  }
  return null;
}

/** Blockieren / Melden im DM-Kopf (Konzept 9: Blockliste und Meldefunktion). */
function BlockMenu({ worldId, partnerId }: { worldId: number; partnerId: number }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const blocks = useQuery(trpc.chat.blocks.queryOptions({ worldId }));
  const blocked = blocks.data?.some((b) => b.playerId === partnerId) ?? false;
  const [reporting, setReporting] = useState(false);
  const [reason, setReason] = useState<(typeof REPORT_REASONS)[number]>('SPAM');
  const [text, setText] = useState('');
  const block = useMutation(
    trpc.chat.block.mutationOptions({
      onSettled: () => qc.invalidateQueries({ queryKey: trpc.chat.pathKey() }),
    }),
  );
  const unblock = useMutation(
    trpc.chat.unblock.mutationOptions({
      onSettled: () => qc.invalidateQueries({ queryKey: trpc.chat.pathKey() }),
    }),
  );
  const report = useMutation(
    trpc.chat.report.mutationOptions({
      onSuccess: () => {
        setReporting(false);
        setText('');
      },
    }),
  );
  return (
    <span className="ml-auto flex items-center gap-1 text-xs font-normal">
      {blocked ? (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => unblock.mutate({ worldId, playerId: partnerId })}
        >
          Blockierung aufheben
        </Button>
      ) : (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => block.mutate({ worldId, playerId: partnerId })}
        >
          Blockieren
        </Button>
      )}
      <Button size="sm" variant="ghost" onClick={() => setReporting((r) => !r)}>
        Melden
      </Button>
      {reporting && (
        <span className="flex items-center gap-1">
          <select
            className="rounded-md border border-stone-700 bg-stone-900 px-1 py-0.5"
            value={reason}
            onChange={(e) => setReason(e.target.value as (typeof REPORT_REASONS)[number])}
          >
            {REPORT_REASONS.map((r) => (
              <option key={r} value={r}>
                {REPORT_REASON_LABELS[r]}
              </option>
            ))}
          </select>
          <input
            className="w-32 rounded-md border border-stone-700 bg-stone-900 px-1 py-0.5"
            placeholder="Grund"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <Button
            size="sm"
            loading={report.isPending}
            onClick={() => report.mutate({ worldId, playerId: partnerId, reason, message: text })}
          >
            Senden
          </Button>
        </span>
      )}
      {report.isSuccess && <span className="text-emerald-400">Gemeldet.</span>}
    </span>
  );
}
